// src/client.ts
import { ALLOWED_ATTACHMENT_TYPES, CLOSE_SESSION_REVOKED, MAX_ATTACHMENT_BYTES } from "@kobecuppens/livechat-protocol/constants";

// src/http.ts
var LiveChatApiError = class extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = "LiveChatApiError";
  }
  status;
  code;
  /** Network failures and 5xx/429 are worth retrying; 4xx validation errors are not. */
  get retryable() {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }
};
var LiveChatHttp = class {
  constructor(opts) {
    this.opts = opts;
    this.base = opts.apiUrl.replace(/\/+$/, "");
    this.doFetch = opts.fetch ?? ((...args) => globalThis.fetch(...args));
  }
  opts;
  base;
  doFetch;
  get apiUrl() {
    return this.base;
  }
  async request(method, path, init = {}, retried = false) {
    const headers = { ...init.headers, "X-Livechat-Key": this.opts.workspaceKey };
    const token = init.auth !== false ? await this.opts.getToken() : null;
    if (token) headers.Authorization = `Bearer ${token}`;
    let body;
    if (init.raw !== void 0) {
      body = init.raw;
      if (init.contentType) headers["Content-Type"] = init.contentType;
    } else if (init.body !== void 0) {
      body = JSON.stringify(init.body);
      headers["Content-Type"] = "application/json";
    }
    let res;
    try {
      res = await this.doFetch(`${this.base}${path}`, { method, headers, body });
    } catch (err) {
      throw new LiveChatApiError(0, "network_error", err instanceof Error ? err.message : "Network request failed");
    }
    if (res.status === 204) return void 0;
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const code = data?.error?.code ?? "http_error";
      if (res.status === 401 && code === "invalid_token" && init.auth !== false && !retried) {
        await this.opts.onInvalidToken(token);
        return this.request(method, path, init, true);
      }
      throw new LiveChatApiError(res.status, code, data?.error?.message ?? res.statusText);
    }
    return data;
  }
  createSession(body) {
    return this.request("POST", "/v1/session", { body, auth: false });
  }
  getConfig() {
    return this.request("GET", "/v1/config", { auth: false });
  }
  listConversations(cursor) {
    return this.request("GET", `/v1/conversations${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`);
  }
  getConversation(id) {
    return this.request("GET", `/v1/conversations/${encodeURIComponent(id)}`);
  }
  getUnreadCount() {
    return this.request("GET", "/v1/conversations/unread");
  }
  startConversation(body) {
    return this.request("POST", "/v1/conversations", { body });
  }
  listMessages(conversationId, before) {
    const q = before ? `?before=${encodeURIComponent(before)}` : "";
    return this.request("GET", `/v1/conversations/${encodeURIComponent(conversationId)}/messages${q}`);
  }
  sendMessage(conversationId, body) {
    return this.request("POST", `/v1/conversations/${encodeURIComponent(conversationId)}/messages`, { body });
  }
  markRead(conversationId) {
    return this.request("POST", `/v1/conversations/${encodeURIComponent(conversationId)}/read`);
  }
  submitCsat(conversationId, body) {
    return this.request("POST", `/v1/conversations/${encodeURIComponent(conversationId)}/csat`, { body });
  }
  registerPushDevice(body) {
    return this.request("POST", "/v1/push-devices", { body });
  }
  unregisterPushDevice(token) {
    return this.request("DELETE", `/v1/push-devices/${encodeURIComponent(token)}`);
  }
  uploadAttachment(file) {
    const headers = { "X-Filename": encodeURIComponent(file.name) };
    if (file.width) headers["X-Width"] = String(Math.round(file.width));
    if (file.height) headers["X-Height"] = String(Math.round(file.height));
    return this.request("POST", "/v1/attachments", { raw: file.body, contentType: file.type, headers });
  }
  // Help center content is public per workspace: no contact token needed.
  listFaqCategories(locale) {
    return this.request("GET", `/v1/faq/categories?locale=${encodeURIComponent(locale)}`, { auth: false });
  }
  listFaqArticles(opts) {
    const q = new URLSearchParams({ locale: opts.locale });
    if (opts.category) q.set("category", opts.category);
    if (opts.sort) q.set("sort", opts.sort);
    if (opts.limit) q.set("limit", String(opts.limit));
    return this.request("GET", `/v1/faq/articles?${q}`, { auth: false });
  }
  searchFaq(query, opts) {
    const q = new URLSearchParams({ q: query, locale: opts.locale, mode: opts.mode ?? "all" });
    if (opts.limit) q.set("limit", String(opts.limit));
    return this.request("GET", `/v1/faq/articles?${q}`, { auth: false });
  }
  getFaqArticle(slug, locale) {
    return this.request("GET", `/v1/faq/articles/${encodeURIComponent(slug)}?locale=${encodeURIComponent(locale)}`, { auth: false });
  }
  sendFaqFeedback(articleId, helpful) {
    return this.request("POST", `/v1/faq/articles/${encodeURIComponent(articleId)}/feedback`, { body: { helpful }, auth: false });
  }
  /** WebSocket URL for a conversation; auth goes in the query since sockets can't set headers. */
  async socketUrl(conversationId) {
    const token = await this.opts.getToken();
    const wsBase = this.base.replace(/^http/, "ws");
    return `${wsBase}/v1/conversations/${encodeURIComponent(conversationId)}/ws?key=${encodeURIComponent(this.opts.workspaceKey)}&token=${encodeURIComponent(token)}`;
  }
};

// src/locales/de.ts
var de = {
  "home.title": "Hilfe",
  "home.greeting": "Hallo \u{1F44B} Wie k\xF6nnen wir helfen?",
  "home.searchPlaceholder": "Hilfe durchsuchen",
  "home.popularArticles": "Beliebte Artikel",
  "home.categories": "Themen durchsuchen",
  "home.startChat": "Schreib uns eine Nachricht",
  "home.recentConversations": "Deine Unterhaltungen",
  "status.online": "Wir sind online",
  "status.replyTime": "Antwortet meist innerhalb von {minutes} Min.",
  "status.offline": "Wir sind gerade nicht da. Hinterlass eine Nachricht, wir melden uns.",
  "faq.noResults": "Keine Artikel zu \u201E{query}\u201C gefunden",
  "faq.helpful": "War dieser Artikel hilfreich?",
  "faq.yes": "Ja",
  "faq.no": "Nein",
  "faq.thanks": "Danke f\xFCr dein Feedback!",
  "faq.stillNeedHelp": "Brauchst du noch Hilfe?",
  "faq.articlesCount": "{count} Artikel",
  "faq.articlesCount.one": "{count} Artikel",
  "faq.articlesCount.other": "{count} Artikel",
  "chat.title": "Chat",
  "chat.newConversation": "Neue Unterhaltung",
  "chat.placeholder": "Nachricht schreiben\u2026",
  "chat.send": "Senden",
  "chat.attach": "Datei anh\xE4ngen",
  "chat.removeAttachment": "{name} entfernen",
  "chat.suggestedArticles": "Diese Artikel k\xF6nnten helfen",
  "chat.typing": "{name} schreibt\u2026",
  "chat.someoneTyping": "Schreibt\u2026",
  "chat.seen": "Gesehen",
  "chat.sending": "Wird gesendet\u2026",
  "chat.failed": "Nicht gesendet. Tippe, um es erneut zu versuchen.",
  "chat.loadOlder": "Fr\xFChere Nachrichten laden",
  "chat.you": "Du",
  "chat.support": "Support",
  "chat.resolved": "Diese Unterhaltung wurde als gel\xF6st markiert",
  "chat.reopened": "Unterhaltung wieder ge\xF6ffnet",
  "chat.assigned": "{name} ist der Unterhaltung beigetreten",
  "chat.empty": "Noch keine Unterhaltungen",
  "chat.attachmentTooLarge": "Dateien m\xFCssen kleiner als {mb} MB sein",
  "chat.attachmentType": "Dieser Dateityp wird nicht unterst\xFCtzt",
  "csat.question": "Wie bewertest du die Unterhaltung?",
  "csat.commentPlaceholder": "Erz\xE4hl uns mehr (optional)",
  "csat.submit": "Absenden",
  "csat.thanks": "Danke f\xFCr deine Bewertung!",
  "launcher.unread": "{title}, {count} ungelesen",
  "chat.unread": "Ungelesen",
  "csat.score": "{score} von 5",
  "common.back": "Zur\xFCck",
  "common.close": "Schlie\xDFen",
  "common.retry": "Erneut versuchen",
  "common.offline": "Du bist offline. Verbindung wird wiederhergestellt\u2026",
  "common.error": "Etwas ist schiefgelaufen",
  "common.loading": "Wird geladen\u2026"
};

// src/locales/en.ts
var en = {
  "home.title": "Help",
  "home.greeting": "Hi there \u{1F44B} How can we help?",
  "home.searchPlaceholder": "Search for help",
  "home.popularArticles": "Popular articles",
  "home.categories": "Browse topics",
  "home.startChat": "Send us a message",
  "home.recentConversations": "Your conversations",
  "status.online": "We're online",
  "status.replyTime": "Typically replies in {minutes} min",
  "status.offline": "We're away right now. Leave a message and we'll get back to you.",
  "faq.noResults": "No articles found for \u201C{query}\u201D",
  "faq.helpful": "Was this article helpful?",
  "faq.yes": "Yes",
  "faq.no": "No",
  "faq.thanks": "Thanks for your feedback!",
  "faq.stillNeedHelp": "Still need help?",
  "faq.articlesCount": "{count} articles",
  "faq.articlesCount.one": "{count} article",
  "faq.articlesCount.other": "{count} articles",
  "chat.title": "Chat",
  "chat.newConversation": "New conversation",
  "chat.placeholder": "Write a message\u2026",
  "chat.send": "Send",
  "chat.attach": "Attach a file",
  "chat.removeAttachment": "Remove {name}",
  "chat.suggestedArticles": "These articles might help",
  "chat.typing": "{name} is typing\u2026",
  "chat.someoneTyping": "Typing\u2026",
  "chat.seen": "Seen",
  "chat.sending": "Sending\u2026",
  "chat.failed": "Not sent. Tap to retry.",
  "chat.loadOlder": "Load earlier messages",
  "chat.you": "You",
  "chat.support": "Support",
  "chat.resolved": "This conversation was marked as resolved",
  "chat.reopened": "Conversation reopened",
  "chat.assigned": "{name} joined the conversation",
  "chat.empty": "No conversations yet",
  "chat.attachmentTooLarge": "Files must be smaller than {mb} MB",
  "chat.attachmentType": "This file type isn't supported",
  "csat.question": "How would you rate your conversation?",
  "csat.commentPlaceholder": "Tell us more (optional)",
  "csat.submit": "Submit",
  "csat.thanks": "Thanks for rating us!",
  "launcher.unread": "{title}, {count} unread",
  "chat.unread": "Unread",
  "csat.score": "{score} of 5",
  "common.back": "Back",
  "common.close": "Close",
  "common.retry": "Retry",
  "common.offline": "You're offline. Reconnecting\u2026",
  "common.error": "Something went wrong",
  "common.loading": "Loading\u2026"
};

// src/locales/es.ts
var es = {
  "home.title": "Ayuda",
  "home.greeting": "Hola \u{1F44B} \xBFC\xF3mo podemos ayudarte?",
  "home.searchPlaceholder": "Buscar ayuda",
  "home.popularArticles": "Art\xEDculos populares",
  "home.categories": "Explorar temas",
  "home.startChat": "Env\xEDanos un mensaje",
  "home.recentConversations": "Tus conversaciones",
  "status.online": "Estamos en l\xEDnea",
  "status.replyTime": "Suele responder en {minutes} min",
  "status.offline": "Ahora no estamos disponibles. D\xE9janos un mensaje y te responderemos.",
  "faq.noResults": "No se encontraron art\xEDculos para \xAB{query}\xBB",
  "faq.helpful": "\xBFTe ha resultado \xFAtil este art\xEDculo?",
  "faq.yes": "S\xED",
  "faq.no": "No",
  "faq.thanks": "\xA1Gracias por tu opini\xF3n!",
  "faq.stillNeedHelp": "\xBFTodav\xEDa necesitas ayuda?",
  "faq.articlesCount": "{count} art\xEDculos",
  "faq.articlesCount.one": "{count} art\xEDculo",
  "faq.articlesCount.other": "{count} art\xEDculos",
  "chat.title": "Chat",
  "chat.newConversation": "Nueva conversaci\xF3n",
  "chat.placeholder": "Escribe un mensaje\u2026",
  "chat.send": "Enviar",
  "chat.attach": "Adjuntar un archivo",
  "chat.removeAttachment": "Quitar {name}",
  "chat.suggestedArticles": "Estos art\xEDculos podr\xEDan ayudarte",
  "chat.typing": "{name} est\xE1 escribiendo\u2026",
  "chat.someoneTyping": "Escribiendo\u2026",
  "chat.seen": "Visto",
  "chat.sending": "Enviando\u2026",
  "chat.failed": "No enviado. Toca para reintentar.",
  "chat.loadOlder": "Cargar mensajes anteriores",
  "chat.you": "T\xFA",
  "chat.support": "Soporte",
  "chat.resolved": "Esta conversaci\xF3n se marc\xF3 como resuelta",
  "chat.reopened": "Conversaci\xF3n reabierta",
  "chat.assigned": "{name} se uni\xF3 a la conversaci\xF3n",
  "chat.empty": "A\xFAn no hay conversaciones",
  "chat.attachmentTooLarge": "Los archivos deben pesar menos de {mb} MB",
  "chat.attachmentType": "Este tipo de archivo no es compatible",
  "csat.question": "\xBFC\xF3mo valorar\xEDas esta conversaci\xF3n?",
  "csat.commentPlaceholder": "Cu\xE9ntanos m\xE1s (opcional)",
  "csat.submit": "Enviar",
  "csat.thanks": "\xA1Gracias por tu valoraci\xF3n!",
  "launcher.unread": "{title}, {count} sin leer",
  "chat.unread": "Sin leer",
  "csat.score": "{score} de 5",
  "common.back": "Volver",
  "common.close": "Cerrar",
  "common.retry": "Reintentar",
  "common.offline": "Est\xE1s sin conexi\xF3n. Reconectando\u2026",
  "common.error": "Algo sali\xF3 mal",
  "common.loading": "Cargando\u2026"
};

// src/locales/fr.ts
var fr = {
  "home.title": "Aide",
  "home.greeting": "Bonjour \u{1F44B} Comment pouvons-nous vous aider ?",
  "home.searchPlaceholder": "Rechercher de l'aide",
  "home.popularArticles": "Articles populaires",
  "home.categories": "Parcourir les sujets",
  "home.startChat": "Envoyez-nous un message",
  "home.recentConversations": "Vos conversations",
  "status.online": "Nous sommes en ligne",
  "status.replyTime": "R\xE9pond g\xE9n\xE9ralement en {minutes} min",
  "status.offline": "Nous sommes absents pour le moment. Laissez un message et nous vous r\xE9pondrons.",
  "faq.noResults": "Aucun article trouv\xE9 pour \xAB {query} \xBB",
  "faq.helpful": "Cet article vous a-t-il \xE9t\xE9 utile ?",
  "faq.yes": "Oui",
  "faq.no": "Non",
  "faq.thanks": "Merci pour votre avis !",
  "faq.stillNeedHelp": "Besoin d'aide suppl\xE9mentaire ?",
  "faq.articlesCount": "{count} articles",
  "faq.articlesCount.one": "{count} article",
  "faq.articlesCount.other": "{count} articles",
  "chat.title": "Chat",
  "chat.newConversation": "Nouvelle conversation",
  "chat.placeholder": "\xC9crivez un message\u2026",
  "chat.send": "Envoyer",
  "chat.attach": "Joindre un fichier",
  "chat.removeAttachment": "Retirer {name}",
  "chat.suggestedArticles": "Ces articles pourraient vous aider",
  "chat.typing": "{name} est en train d'\xE9crire\u2026",
  "chat.someoneTyping": "En train d'\xE9crire\u2026",
  "chat.seen": "Vu",
  "chat.sending": "Envoi\u2026",
  "chat.failed": "Non envoy\xE9. Touchez pour r\xE9essayer.",
  "chat.loadOlder": "Charger les messages pr\xE9c\xE9dents",
  "chat.you": "Vous",
  "chat.support": "Support",
  "chat.resolved": "Cette conversation a \xE9t\xE9 marqu\xE9e comme r\xE9solue",
  "chat.reopened": "Conversation rouverte",
  "chat.assigned": "{name} a rejoint la conversation",
  "chat.empty": "Aucune conversation pour l'instant",
  "chat.attachmentTooLarge": "Les fichiers doivent faire moins de {mb} Mo",
  "chat.attachmentType": "Ce type de fichier n'est pas pris en charge",
  "csat.question": "Comment \xE9valuez-vous cette conversation ?",
  "csat.commentPlaceholder": "Dites-nous en plus (facultatif)",
  "csat.submit": "Envoyer",
  "csat.thanks": "Merci pour votre \xE9valuation !",
  "launcher.unread": "{title}, {count} non lus",
  "chat.unread": "Non lu",
  "csat.score": "{score} sur 5",
  "common.back": "Retour",
  "common.close": "Fermer",
  "common.retry": "R\xE9essayer",
  "common.offline": "Vous \xEAtes hors ligne. Reconnexion\u2026",
  "common.error": "Une erreur s'est produite",
  "common.loading": "Chargement\u2026"
};

// src/locales/ja.ts
var ja = {
  "home.title": "\u30D8\u30EB\u30D7",
  "home.greeting": "\u3053\u3093\u306B\u3061\u306F \u{1F44B} \u3069\u306E\u3088\u3046\u306A\u3054\u7528\u4EF6\u3067\u3057\u3087\u3046\u304B\uFF1F",
  "home.searchPlaceholder": "\u30D8\u30EB\u30D7\u3092\u691C\u7D22",
  "home.popularArticles": "\u4EBA\u6C17\u306E\u8A18\u4E8B",
  "home.categories": "\u30C8\u30D4\u30C3\u30AF\u3092\u898B\u308B",
  "home.startChat": "\u30E1\u30C3\u30BB\u30FC\u30B8\u3092\u9001\u308B",
  "home.recentConversations": "\u3042\u306A\u305F\u306E\u4F1A\u8A71",
  "status.online": "\u30AA\u30F3\u30E9\u30A4\u30F3\u5BFE\u5FDC\u4E2D",
  "status.replyTime": "\u901A\u5E38{minutes}\u5206\u4EE5\u5185\u306B\u8FD4\u4FE1\u3057\u307E\u3059",
  "status.offline": "\u305F\u3060\u3044\u307E\u5BFE\u5FDC\u6642\u9593\u5916\u3067\u3059\u3002\u30E1\u30C3\u30BB\u30FC\u30B8\u3092\u6B8B\u3057\u3066\u3044\u305F\u3060\u3051\u308C\u3070\u3001\u6298\u308A\u8FD4\u3057\u3054\u9023\u7D61\u3057\u307E\u3059\u3002",
  "faq.noResults": "\u300C{query}\u300D\u306B\u4E00\u81F4\u3059\u308B\u8A18\u4E8B\u306F\u898B\u3064\u304B\u308A\u307E\u305B\u3093\u3067\u3057\u305F",
  "faq.helpful": "\u3053\u306E\u8A18\u4E8B\u306F\u5F79\u306B\u7ACB\u3061\u307E\u3057\u305F\u304B\uFF1F",
  "faq.yes": "\u306F\u3044",
  "faq.no": "\u3044\u3044\u3048",
  "faq.thanks": "\u30D5\u30A3\u30FC\u30C9\u30D0\u30C3\u30AF\u3042\u308A\u304C\u3068\u3046\u3054\u3056\u3044\u307E\u3059\uFF01",
  "faq.stillNeedHelp": "\u89E3\u6C7A\u3057\u307E\u305B\u3093\u3067\u3057\u305F\u304B\uFF1F",
  "faq.articlesCount": "{count}\u4EF6\u306E\u8A18\u4E8B",
  "faq.articlesCount.one": "{count}\u4EF6\u306E\u8A18\u4E8B",
  "faq.articlesCount.other": "{count}\u4EF6\u306E\u8A18\u4E8B",
  "chat.title": "\u30C1\u30E3\u30C3\u30C8",
  "chat.newConversation": "\u65B0\u3057\u3044\u4F1A\u8A71",
  "chat.placeholder": "\u30E1\u30C3\u30BB\u30FC\u30B8\u3092\u5165\u529B\u2026",
  "chat.send": "\u9001\u4FE1",
  "chat.attach": "\u30D5\u30A1\u30A4\u30EB\u3092\u6DFB\u4ED8",
  "chat.removeAttachment": "{name} \u3092\u524A\u9664",
  "chat.suggestedArticles": "\u3053\u3061\u3089\u306E\u8A18\u4E8B\u304C\u5F79\u306B\u7ACB\u3064\u304B\u3082\u3057\u308C\u307E\u305B\u3093",
  "chat.typing": "{name} \u304C\u5165\u529B\u4E2D\u2026",
  "chat.someoneTyping": "\u5165\u529B\u4E2D\u2026",
  "chat.seen": "\u65E2\u8AAD",
  "chat.sending": "\u9001\u4FE1\u4E2D\u2026",
  "chat.failed": "\u9001\u4FE1\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002\u30BF\u30C3\u30D7\u3057\u3066\u518D\u8A66\u884C\u3057\u3066\u304F\u3060\u3055\u3044\u3002",
  "chat.loadOlder": "\u4EE5\u524D\u306E\u30E1\u30C3\u30BB\u30FC\u30B8\u3092\u8AAD\u307F\u8FBC\u3080",
  "chat.you": "\u3042\u306A\u305F",
  "chat.support": "\u30B5\u30DD\u30FC\u30C8",
  "chat.resolved": "\u3053\u306E\u4F1A\u8A71\u306F\u89E3\u6C7A\u6E08\u307F\u306B\u306A\u308A\u307E\u3057\u305F",
  "chat.reopened": "\u4F1A\u8A71\u304C\u518D\u958B\u3055\u308C\u307E\u3057\u305F",
  "chat.assigned": "{name} \u304C\u4F1A\u8A71\u306B\u53C2\u52A0\u3057\u307E\u3057\u305F",
  "chat.empty": "\u4F1A\u8A71\u306F\u307E\u3060\u3042\u308A\u307E\u305B\u3093",
  "chat.attachmentTooLarge": "\u30D5\u30A1\u30A4\u30EB\u30B5\u30A4\u30BA\u306F{mb}MB\u672A\u6E80\u306B\u3057\u3066\u304F\u3060\u3055\u3044",
  "chat.attachmentType": "\u3053\u306E\u30D5\u30A1\u30A4\u30EB\u5F62\u5F0F\u306B\u306F\u5BFE\u5FDC\u3057\u3066\u3044\u307E\u305B\u3093",
  "csat.question": "\u4ECA\u56DE\u306E\u4F1A\u8A71\u306F\u3044\u304B\u304C\u3067\u3057\u305F\u304B\uFF1F",
  "csat.commentPlaceholder": "\u3054\u610F\u898B\u3092\u304A\u805E\u304B\u305B\u304F\u3060\u3055\u3044\uFF08\u4EFB\u610F\uFF09",
  "csat.submit": "\u9001\u4FE1",
  "csat.thanks": "\u8A55\u4FA1\u3042\u308A\u304C\u3068\u3046\u3054\u3056\u3044\u307E\u3059\uFF01",
  "launcher.unread": "{title}\u3001\u672A\u8AAD{count}\u4EF6",
  "chat.unread": "\u672A\u8AAD",
  "csat.score": "5\u6BB5\u968E\u4E2D{score}",
  "common.back": "\u623B\u308B",
  "common.close": "\u9589\u3058\u308B",
  "common.retry": "\u518D\u8A66\u884C",
  "common.offline": "\u30AA\u30D5\u30E9\u30A4\u30F3\u3067\u3059\u3002\u518D\u63A5\u7D9A\u3057\u3066\u3044\u307E\u3059\u2026",
  "common.error": "\u554F\u984C\u304C\u767A\u751F\u3057\u307E\u3057\u305F",
  "common.loading": "\u8AAD\u307F\u8FBC\u307F\u4E2D\u2026"
};

// src/locales/ko.ts
var ko = {
  "home.title": "\uB3C4\uC6C0\uB9D0",
  "home.greeting": "\uC548\uB155\uD558\uC138\uC694 \u{1F44B} \uBB34\uC5C7\uC744 \uB3C4\uC640\uB4DC\uB9B4\uAE4C\uC694?",
  "home.searchPlaceholder": "\uB3C4\uC6C0\uB9D0 \uAC80\uC0C9",
  "home.popularArticles": "\uC778\uAE30 \uBB38\uC11C",
  "home.categories": "\uC8FC\uC81C \uB458\uB7EC\uBCF4\uAE30",
  "home.startChat": "\uBA54\uC2DC\uC9C0 \uBCF4\uB0B4\uAE30",
  "home.recentConversations": "\uB0B4 \uB300\uD654",
  "status.online": "\uC9C0\uAE08 \uC0C1\uB2F4 \uAC00\uB2A5\uD569\uB2C8\uB2E4",
  "status.replyTime": "\uBCF4\uD1B5 {minutes}\uBD84 \uC548\uC5D0 \uB2F5\uBCC0\uD569\uB2C8\uB2E4",
  "status.offline": "\uC9C0\uAE08\uC740 \uC790\uB9AC\uB97C \uBE44\uC6E0\uC2B5\uB2C8\uB2E4. \uBA54\uC2DC\uC9C0\uB97C \uB0A8\uACA8 \uC8FC\uC2DC\uBA74 \uB2F5\uBCC0\uB4DC\uB9AC\uACA0\uC2B5\uB2C8\uB2E4.",
  "faq.noResults": "\u201C{query}\u201D\uC5D0 \uB300\uD55C \uBB38\uC11C\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
  "faq.helpful": "\uC774 \uBB38\uC11C\uAC00 \uB3C4\uC6C0\uC774 \uB418\uC5C8\uB098\uC694?",
  "faq.yes": "\uC608",
  "faq.no": "\uC544\uB2C8\uC694",
  "faq.thanks": "\uC758\uACAC\uC744 \uBCF4\uB0B4 \uC8FC\uC154\uC11C \uAC10\uC0AC\uD569\uB2C8\uB2E4!",
  "faq.stillNeedHelp": "\uB3C4\uC6C0\uC774 \uB354 \uD544\uC694\uD558\uC2E0\uAC00\uC694?",
  "faq.articlesCount": "\uBB38\uC11C {count}\uAC1C",
  "faq.articlesCount.one": "\uBB38\uC11C {count}\uAC1C",
  "faq.articlesCount.other": "\uBB38\uC11C {count}\uAC1C",
  "chat.title": "\uCC44\uD305",
  "chat.newConversation": "\uC0C8 \uB300\uD654",
  "chat.placeholder": "\uBA54\uC2DC\uC9C0\uB97C \uC785\uB825\uD558\uC138\uC694\u2026",
  "chat.send": "\uBCF4\uB0B4\uAE30",
  "chat.attach": "\uD30C\uC77C \uCCA8\uBD80",
  "chat.removeAttachment": "{name} \uC81C\uAC70",
  "chat.suggestedArticles": "\uC774 \uBB38\uC11C\uAC00 \uB3C4\uC6C0\uC774 \uB420 \uC218 \uC788\uC2B5\uB2C8\uB2E4",
  "chat.typing": "{name} \uB2D8\uC774 \uC785\uB825 \uC911\u2026",
  "chat.someoneTyping": "\uC785\uB825 \uC911\u2026",
  "chat.seen": "\uC77D\uC74C",
  "chat.sending": "\uBCF4\uB0B4\uB294 \uC911\u2026",
  "chat.failed": "\uC804\uC1A1\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4. \uD0ED\uD558\uC5EC \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC138\uC694.",
  "chat.loadOlder": "\uC774\uC804 \uBA54\uC2DC\uC9C0 \uBD88\uB7EC\uC624\uAE30",
  "chat.you": "\uB098",
  "chat.support": "\uC9C0\uC6D0\uD300",
  "chat.resolved": "\uC774 \uB300\uD654\uB294 \uD574\uACB0\uB428\uC73C\uB85C \uD45C\uC2DC\uB418\uC5C8\uC2B5\uB2C8\uB2E4",
  "chat.reopened": "\uB300\uD654\uAC00 \uB2E4\uC2DC \uC5F4\uB838\uC2B5\uB2C8\uB2E4",
  "chat.assigned": "{name} \uB2D8\uC774 \uB300\uD654\uC5D0 \uCC38\uC5EC\uD588\uC2B5\uB2C8\uB2E4",
  "chat.empty": "\uC544\uC9C1 \uB300\uD654\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
  "chat.attachmentTooLarge": "\uD30C\uC77C\uC740 {mb}MB\uBCF4\uB2E4 \uC791\uC544\uC57C \uD569\uB2C8\uB2E4",
  "chat.attachmentType": "\uC9C0\uC6D0\uD558\uC9C0 \uC54A\uB294 \uD30C\uC77C \uD615\uC2DD\uC785\uB2C8\uB2E4",
  "csat.question": "\uC774\uBC88 \uB300\uD654\uB97C \uD3C9\uAC00\uD574 \uC8FC\uC138\uC694",
  "csat.commentPlaceholder": "\uC758\uACAC\uC744 \uB354 \uB4E4\uB824\uC8FC\uC138\uC694(\uC120\uD0DD)",
  "csat.submit": "\uC81C\uCD9C",
  "csat.thanks": "\uD3C9\uAC00\uD574 \uC8FC\uC154\uC11C \uAC10\uC0AC\uD569\uB2C8\uB2E4!",
  "launcher.unread": "{title}, \uC77D\uC9C0 \uC54A\uC74C {count}\uAC1C",
  "chat.unread": "\uC77D\uC9C0 \uC54A\uC74C",
  "csat.score": "5\uC810 \uC911 {score}\uC810",
  "common.back": "\uB4A4\uB85C",
  "common.close": "\uB2EB\uAE30",
  "common.retry": "\uB2E4\uC2DC \uC2DC\uB3C4",
  "common.offline": "\uC624\uD504\uB77C\uC778 \uC0C1\uD0DC\uC785\uB2C8\uB2E4. \uB2E4\uC2DC \uC5F0\uACB0\uD558\uB294 \uC911\u2026",
  "common.error": "\uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
  "common.loading": "\uBD88\uB7EC\uC624\uB294 \uC911\u2026"
};

// src/locales/nl.ts
var nl = {
  "home.title": "Help",
  "home.greeting": "Hallo \u{1F44B} Hoe kunnen we helpen?",
  "home.searchPlaceholder": "Zoek in het helpcentrum",
  "home.popularArticles": "Populaire artikels",
  "home.categories": "Onderwerpen",
  "home.startChat": "Stuur ons een bericht",
  "home.recentConversations": "Jouw gesprekken",
  "status.online": "We zijn online",
  "status.replyTime": "Antwoordt meestal binnen {minutes} min",
  "status.offline": "We zijn momenteel afwezig. Laat een bericht achter en we komen bij je terug.",
  "faq.noResults": "Geen artikels gevonden voor \u201C{query}\u201D",
  "faq.helpful": "Was dit artikel nuttig?",
  "faq.yes": "Ja",
  "faq.no": "Nee",
  "faq.thanks": "Bedankt voor je feedback!",
  "faq.stillNeedHelp": "Nog hulp nodig?",
  "faq.articlesCount": "{count} artikels",
  "faq.articlesCount.one": "{count} artikel",
  "faq.articlesCount.other": "{count} artikels",
  "chat.title": "Chat",
  "chat.newConversation": "Nieuw gesprek",
  "chat.placeholder": "Schrijf een bericht\u2026",
  "chat.send": "Verstuur",
  "chat.attach": "Bestand toevoegen",
  "chat.removeAttachment": "{name} verwijderen",
  "chat.suggestedArticles": "Deze artikels kunnen helpen",
  "chat.typing": "{name} is aan het typen\u2026",
  "chat.someoneTyping": "Aan het typen\u2026",
  "chat.seen": "Gezien",
  "chat.sending": "Versturen\u2026",
  "chat.failed": "Niet verstuurd. Tik om opnieuw te proberen.",
  "chat.loadOlder": "Oudere berichten laden",
  "chat.you": "Jij",
  "chat.support": "Support",
  "chat.resolved": "Dit gesprek werd als opgelost gemarkeerd",
  "chat.reopened": "Gesprek heropend",
  "chat.assigned": "{name} neemt deel aan het gesprek",
  "chat.empty": "Nog geen gesprekken",
  "chat.attachmentTooLarge": "Bestanden moeten kleiner zijn dan {mb} MB",
  "chat.attachmentType": "Dit bestandstype wordt niet ondersteund",
  "csat.question": "Hoe beoordeel je dit gesprek?",
  "csat.commentPlaceholder": "Vertel ons meer (optioneel)",
  "csat.submit": "Verstuur",
  "csat.thanks": "Bedankt voor je beoordeling!",
  "launcher.unread": "{title}, {count} ongelezen",
  "chat.unread": "Ongelezen",
  "csat.score": "{score} van 5",
  "common.back": "Terug",
  "common.close": "Sluiten",
  "common.retry": "Opnieuw",
  "common.offline": "Je bent offline. Opnieuw verbinden\u2026",
  "common.error": "Er ging iets mis",
  "common.loading": "Laden\u2026"
};

// src/i18n.ts
var BUILT_IN = { en, de, es, fr, ja, ko, nl };
function localeChain(locale) {
  const chain = [locale];
  const base = locale.split("-")[0];
  if (base !== locale) chain.push(base);
  if (!chain.includes("en")) chain.push("en");
  return chain;
}
function negotiateLocale(preferred, supported, fallback) {
  if (!preferred) return fallback;
  const base = preferred.split("-")[0];
  for (const candidate of base === preferred ? [preferred] : [preferred, base]) {
    if (supported.includes(candidate)) return candidate;
  }
  return fallback;
}
function pluralRules(locale) {
  if (typeof Intl !== "undefined" && typeof Intl.PluralRules === "function") return new Intl.PluralRules(locale);
  const base = locale.split("-")[0];
  if (base === "ja" || base === "ko") return { select: () => "other" };
  return { select: (count) => count === 1 || base === "fr" && count === 0 ? "one" : "other" };
}
function createTranslator(locale, overrides = {}) {
  const chain = localeChain(locale);
  const plurals = pluralRules(locale);
  const lookup = (key) => {
    for (const l of chain) {
      const hit = overrides[l]?.[key] ?? BUILT_IN[l]?.[key];
      if (hit !== void 0) return hit;
    }
    return void 0;
  };
  return (key, vars) => {
    const pluralKey = typeof vars?.count === "number" ? `${key}.${plurals.select(vars.count)}` : null;
    const text = (pluralKey && lookup(pluralKey)) ?? lookup(key) ?? en[key];
    return vars ? text.replace(/\{(\w+)\}/g, (m, name) => name in vars ? String(vars[name]) : m) : text;
  };
}

// src/random.ts
function secureRandomId(bytes = 16) {
  const c = globalThis.crypto;
  if (!c?.getRandomValues) {
    throw new Error(
      "[livechat] crypto.getRandomValues is not available. On React Native, import 'react-native-get-random-values' before the SDK."
    );
  }
  return [...c.getRandomValues(new Uint8Array(bytes))].map((b) => b.toString(16).padStart(2, "0")).join("");
}

// src/socket.ts
var ReconnectingSocket = class {
  constructor(opts) {
    this.opts = opts;
  }
  opts;
  ws = null;
  attempts = 0;
  everOpened = false;
  stopped = false;
  /** True from connect() start until the socket opens or fails, so nudge() can't start a second one. */
  connecting = false;
  reconnectTimer = null;
  pingTimer = null;
  start() {
    this.stopped = false;
    void this.connect();
  }
  stop() {
    this.stopped = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.reconnectTimer = null;
    this.pingTimer = null;
    const ws = this.ws;
    this.ws = null;
    ws?.close(1e3);
    this.opts.onStateChange("closed");
  }
  /** Reconnect immediately (e.g. app came to foreground or network came back). */
  nudge() {
    if (this.stopped || this.connecting || this.ws?.readyState === 1 || this.ws?.readyState === 0) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.attempts = 0;
    void this.connect();
  }
  send(event) {
    if (this.ws?.readyState !== 1) return false;
    this.ws.send(JSON.stringify(event));
    return true;
  }
  async connect() {
    if (this.stopped || this.connecting) return;
    this.connecting = true;
    this.opts.onStateChange("connecting");
    let ws;
    try {
      const url = await this.opts.url();
      if (this.stopped) return;
      const WS = this.opts.WebSocket ?? globalThis.WebSocket;
      ws = new WS(url);
    } catch {
      this.scheduleReconnect();
      return;
    } finally {
      this.connecting = false;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      const isReconnect = this.everOpened;
      this.everOpened = true;
      this.attempts = 0;
      this.opts.onStateChange("open");
      this.pingTimer = setInterval(() => this.send({ type: "ping" }), this.opts.pingIntervalMs ?? 25e3);
      if (isReconnect) this.opts.onReconnect();
    };
    ws.onmessage = (e) => {
      if (this.ws !== ws || typeof e.data !== "string") return;
      let event;
      try {
        event = JSON.parse(e.data);
      } catch {
        return;
      }
      this.opts.onEvent(event);
    };
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      const code = e?.code ?? 1006;
      if (code >= 4e3 && code < 4500) {
        this.stopped = true;
        this.opts.onStateChange("closed");
        this.opts.onTerminalClose?.(code);
        return;
      }
      if (!this.stopped) this.scheduleReconnect();
    };
    ws.onerror = () => {
    };
  }
  scheduleReconnect() {
    if (this.stopped) return;
    this.opts.onStateChange("connecting");
    const max = this.opts.maxBackoffMs ?? 3e4;
    const base = Math.min(max, 500 * 2 ** this.attempts);
    const delay = base / 2 + Math.random() * (base / 2);
    this.attempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }
};

// src/storage.ts
function createMemoryStorage() {
  const map = /* @__PURE__ */ new Map();
  return {
    getItem: async (k) => map.get(k) ?? null,
    setItem: async (k, v) => void map.set(k, v),
    removeItem: async (k) => void map.delete(k)
  };
}
function createWebStorage() {
  try {
    const ls = globalThis.localStorage;
    const probe = "__livechat_probe__";
    ls.setItem(probe, "1");
    ls.removeItem(probe);
    return {
      getItem: async (k) => ls.getItem(k),
      setItem: async (k, v) => ls.setItem(k, v),
      removeItem: async (k) => ls.removeItem(k)
    };
  } catch {
    return createMemoryStorage();
  }
}

// src/store.ts
var Store = class {
  constructor(state) {
    this.state = state;
  }
  state;
  listeners = /* @__PURE__ */ new Set();
  getSnapshot = () => this.state;
  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  set(update) {
    const patch = typeof update === "function" ? update(this.state) : update;
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }
};

// src/client.ts
var MAX_WATCHED_CONVERSATIONS = 5;
var DRAFT_CONVERSATION = "draft";
var TYPING_RESEND_MS = 3e3;
var TYPING_IDLE_MS = 5e3;
var REMOTE_TYPING_TTL_MS = 6e3;
var READ_DEBOUNCE_MS = 1e3;
var EMPTY_THREAD = Object.freeze({
  messages: [],
  hasOlder: false,
  loading: false,
  loaded: false,
  typing: null,
  agentLastReadAt: 0,
  connection: "closed",
  error: false
});
var emptyThread = () => ({ ...EMPTY_THREAD, messages: [] });
function sortMessages(messages) {
  const sent = messages.filter((m) => m.status === "sent").sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  return [...sent, ...messages.filter((m) => m.status !== "sent")];
}
function mergeMessages(existing, incoming) {
  const byId = new Map(existing.map((m) => [m.id, m]));
  for (const msg of incoming) {
    if (msg.clientId) {
      for (const [id, m] of byId) {
        if (m.status !== "sent" && m.clientId === msg.clientId) byId.delete(id);
      }
    }
    byId.set(msg.id, { ...msg, status: "sent" });
  }
  return sortMessages([...byId.values()]);
}
var LiveChatClient = class {
  constructor(opts) {
    this.opts = opts;
    this.storage = opts.storage ?? createMemoryStorage();
    this.storagePrefix = `livechat:${opts.workspaceKey}:`;
    this.user = opts.user ?? null;
    this.preferredLocale = opts.locale;
    this.store = new Store({
      status: "idle",
      error: null,
      config: null,
      contact: null,
      locale: opts.locale?.split("-")[0] ?? "en",
      conversations: [],
      conversationsLoaded: false,
      unreadCount: 0,
      threads: {},
      identity: 0
    });
    this.http = new LiveChatHttp({
      apiUrl: opts.apiUrl,
      workspaceKey: opts.workspaceKey,
      fetch: opts.fetch,
      getToken: async () => (await this.ensureSession()).token,
      onInvalidToken: (failedToken) => this.replaceSession(failedToken)
    });
  }
  opts;
  store;
  http;
  storage;
  storagePrefix;
  user;
  preferredLocale;
  session = null;
  sessionPromise = null;
  /** Bumped on identity change; a session request from an older generation must not commit. */
  sessionGen = 0;
  initPromise = null;
  sockets = /* @__PURE__ */ new Map();
  typingOut = /* @__PURE__ */ new Map();
  typingIn = /* @__PURE__ */ new Map();
  /** Set while a draft is creating the conversation; resolves with its id (null on failure). */
  startingConversation = null;
  /**
   * Bumped whenever identity changes. Async work captures it and drops its result if it
   * changed meanwhile, so a previous user's responses can't land in the new user's state.
   */
  epoch = 0;
  readTimers = /* @__PURE__ */ new Map();
  /**
   * In-flight loads per conversation. A load requested while one runs is coalesced into a single
   * follow-up after it (its read may predate whatever triggered the new request).
   */
  loads = /* @__PURE__ */ new Map();
  /** Last registered push device, moved to the new identity on login/logout. */
  pushDevice = null;
  /** The push device still has to be registered with the current identity (a move failed). */
  pushPending = false;
  /** identify() calls run one at a time, in call order, so the latest call wins. */
  identifyQueue = Promise.resolve();
  /**
   * Background sockets on the contact's recent conversations while the host app is in the
   * foreground (`startWatching`), so agent replies update the badge (and reach
   * `onAgentMessage` listeners) on any screen. Unlike `openConversation` they don't mark read.
   */
  watchSockets = /* @__PURE__ */ new Map();
  watching = false;
  /** Agent messages already counted by a watch socket (a reconnect backfill must not count them twice). */
  watchSeen = /* @__PURE__ */ new Set();
  agentMessageListeners = /* @__PURE__ */ new Set();
  /** Bodies of pending sends, kept for retry. */
  outbox = /* @__PURE__ */ new Map();
  /** Drops a session the server rejected and gets a new one. */
  async replaceSession(failedToken) {
    if (this.session && this.session.token !== failedToken) return;
    this.session = null;
    await this.storage.removeItem(this.key("session"));
    await this.ensureSession();
  }
  get state() {
    return this.store.getSnapshot();
  }
  // ---------------------------------------------------------------- lifecycle
  /** Loads config and the contact session. Safe to call repeatedly. */
  init() {
    this.initPromise ??= (async () => {
      this.store.set({ status: "initializing", error: null });
      try {
        const [config] = await Promise.all([this.http.getConfig(), this.user ? this.ensureSession() : this.restoreSession()]);
        this.store.set({
          config,
          locale: negotiateLocale(this.preferredLocale, config.locales, config.defaultLocale),
          status: "ready"
        });
        void this.refreshUnread();
        if (this.watching) void this.syncWatch();
      } catch (err) {
        this.initPromise = null;
        this.store.set({ status: "error", error: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    })();
    return this.initPromise;
  }
  destroy() {
    for (const { socket } of this.sockets.values()) socket.stop();
    this.sockets.clear();
    this.stopWatching();
    this.clearTimers();
  }
  clearTimers() {
    for (const t of this.typingIn.values()) clearTimeout(t);
    for (const t of this.typingOut.values()) if (t.idleTimer) clearTimeout(t.idleTimer);
    for (const t of this.readTimers.values()) clearTimeout(t);
    this.typingIn.clear();
    this.typingOut.clear();
    this.readTimers.clear();
  }
  /** Switches identity. Passing null logs out to a fresh anonymous contact. */
  identify(user) {
    const run = this.identifyQueue.catch(() => {
    }).then(() => this.doIdentify(user));
    this.identifyQueue = run;
    return run;
  }
  async doIdentify(user) {
    const sameUser = (user?.id ?? null) === (this.user?.id ?? null);
    if (sameUser && this.session) {
      this.user = user;
      return;
    }
    if (sameUser && !user) return;
    const previous = this.session;
    const device = this.pushDevice;
    if (device && previous) await this.http.unregisterPushDevice(device.token).catch(() => {
    });
    if (device) this.pushPending = true;
    this.user = user;
    this.session = null;
    this.sessionPromise = null;
    this.sessionGen++;
    if (!user) {
      await this.storage.removeItem(this.key("session"));
      if (previous?.userId) await this.storage.removeItem(this.key("deviceId"));
    }
    this.resetConversationState();
    await this.ensureSession(previous && !previous.userId ? previous.token : void 0);
    void this.syncPushDevice();
    void this.refreshUnread();
    if (this.watching) void this.syncWatch();
  }
  /** Registers the push device with the current identity if a previous attempt didn't. */
  async syncPushDevice() {
    const device = this.pushDevice;
    if (!device || !this.pushPending) return;
    this.pushPending = false;
    try {
      await this.http.registerPushDevice(device);
    } catch {
      if (this.pushDevice === device) this.pushPending = true;
    }
  }
  setLocale(locale) {
    this.preferredLocale = locale;
    const config = this.state.config;
    this.store.set({ locale: config ? negotiateLocale(locale, config.locales, config.defaultLocale) : locale });
  }
  // ---------------------------------------------------------------- session
  key(name) {
    return this.storagePrefix + name;
  }
  async deviceId() {
    let id = await this.storage.getItem(this.key("deviceId"));
    if (!id) {
      id = secureRandomId();
      await this.storage.setItem(this.key("deviceId"), id);
    }
    return id;
  }
  isValidSession(s) {
    return !!s && s.expiresAt - Date.now() > 6e4 && s.userId === (this.user?.id ?? null);
  }
  async readStoredSession() {
    const stored = await this.storage.getItem(this.key("session"));
    try {
      return stored ? JSON.parse(stored) : null;
    } catch {
      await this.storage.removeItem(this.key("session"));
      return null;
    }
  }
  /** Loads a still-valid stored session without touching the network. */
  async restoreSession() {
    const parsed = await this.readStoredSession();
    if (parsed && this.isValidSession(parsed) && !this.session) {
      this.session = parsed;
      this.store.set({ contact: parsed.contact });
    }
  }
  ensureSession(previousToken) {
    if (this.session && this.isValidSession(this.session)) return Promise.resolve(this.session);
    if (this.sessionPromise) return this.sessionPromise;
    const gen = this.sessionGen;
    let pending;
    pending = (async () => {
      try {
        const parsed = await this.readStoredSession();
        if (gen !== this.sessionGen) return this.ensureSession();
        if (parsed && this.isValidSession(parsed)) {
          this.session = parsed;
          this.store.set({ contact: parsed.contact });
          return parsed;
        }
        const prevAnon = previousToken ?? (parsed && !parsed.userId && this.user ? parsed.token : void 0);
        const res = await this.http.createSession({
          deviceId: await this.deviceId(),
          ...this.user ? { userId: this.user.id, userHash: this.user.hash, email: this.user.email, name: this.user.name } : {},
          ...this.preferredLocale ? { locale: this.preferredLocale.split("-")[0] } : {},
          ...prevAnon ? { previousToken: prevAnon } : {}
        });
        if (gen !== this.sessionGen) return this.ensureSession();
        const session = { ...res, userId: this.user?.id ?? null };
        this.session = session;
        await this.storage.setItem(this.key("session"), JSON.stringify(session));
        this.store.set({ contact: session.contact });
        return session;
      } finally {
        if (this.sessionPromise === pending) this.sessionPromise = null;
      }
    })();
    this.sessionPromise = pending;
    return pending;
  }
  resetConversationState() {
    this.epoch++;
    for (const { socket } of this.sockets.values()) socket.stop();
    this.sockets.clear();
    for (const socket of this.watchSockets.values()) socket.stop();
    this.watchSockets.clear();
    this.watchSeen.clear();
    this.clearTimers();
    this.outbox.clear();
    this.startingConversation = null;
    this.loads.clear();
    this.store.set((s) => ({ conversations: [], conversationsLoaded: false, unreadCount: 0, threads: {}, identity: s.identity + 1 }));
  }
  // ---------------------------------------------------------------- conversations
  async refreshConversations() {
    const epoch = this.epoch;
    const page = await this.http.listConversations();
    if (epoch !== this.epoch) return;
    this.store.set({ conversations: page.items, conversationsLoaded: true });
  }
  /** Whether a contact session exists (restored or created); before that there's nothing unread. */
  get hasSession() {
    return this.session !== null;
  }
  async refreshUnread() {
    if (!this.session) return;
    const epoch = this.epoch;
    try {
      const { count } = await this.http.getUnreadCount();
      if (epoch !== this.epoch) return;
      this.store.set({ unreadCount: count });
    } catch {
    }
  }
  updateThread(conversationId, update) {
    this.store.set((s) => {
      const prev = s.threads[conversationId] ?? emptyThread();
      return { threads: { ...s.threads, [conversationId]: { ...prev, ...update(prev) } } };
    });
  }
  upsertConversation(conversation) {
    const isNew = !this.state.conversations.some((c) => c.id === conversation.id);
    this.store.set((s) => {
      const rest = s.conversations.filter((c) => c.id !== conversation.id);
      return { conversations: [conversation, ...rest].sort((a, b) => b.lastMessageAt - a.lastMessageAt) };
    });
    if (isNew && this.watching) void this.syncWatch();
  }
  patchConversation(id, patch) {
    this.store.set((s) => ({
      conversations: s.conversations.map((c) => c.id === id ? { ...c, ...patch } : c).sort((a, b) => b.lastMessageAt - a.lastMessageAt)
    }));
  }
  /**
   * Opens a conversation: loads the latest messages, connects realtime and marks it read.
   * Returns a function that releases it (the socket closes when nothing holds it).
   */
  openConversation(conversationId) {
    const entry = this.sockets.get(conversationId);
    if (entry) {
      entry.refs++;
    } else {
      this.watchSockets.get(conversationId)?.stop();
      this.watchSockets.delete(conversationId);
      let failedAttempts = 0;
      let opened = false;
      const socket = new ReconnectingSocket({
        url: async () => {
          if (failedAttempts > 0 && failedAttempts % 3 === 0) await this.http.getUnreadCount().catch(() => {
          });
          failedAttempts++;
          return this.http.socketUrl(conversationId);
        },
        WebSocket: this.opts.WebSocket,
        onEvent: (e) => this.handleEvent(conversationId, e),
        onStateChange: (connection) => {
          this.updateThread(conversationId, () => ({ connection }));
          if (connection !== "open") return;
          failedAttempts = 0;
          if (!opened) {
            opened = true;
            void this.loadLatest(conversationId, "messages");
          }
        },
        onReconnect: () => void this.loadLatest(conversationId),
        // The server revoked this session (identity secret rotated): get a new one and come back.
        onTerminalClose: (code) => {
          if (code !== CLOSE_SESSION_REVOKED) return;
          const epoch = this.epoch;
          const failed = this.session?.token;
          void (failed ? this.replaceSession(failed) : this.ensureSession().then(() => {
          })).catch(() => {
          }).then(() => {
            if (epoch === this.epoch && this.sockets.get(conversationId)?.socket === socket) socket.start();
          });
        }
      });
      this.sockets.set(conversationId, { socket, refs: 1 });
      socket.start();
      void this.loadLatest(conversationId);
    }
    void this.markRead(conversationId);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const e = this.sockets.get(conversationId);
      if (!e) return;
      if (--e.refs <= 0) {
        e.socket.stop();
        this.sockets.delete(conversationId);
        this.setTyping(conversationId, false);
        if (this.watching) void this.syncWatch();
      }
    };
  }
  /** Reconnect all sockets now, e.g. when the app returns to the foreground. */
  resume() {
    for (const { socket } of this.sockets.values()) socket.nudge();
    for (const socket of this.watchSockets.values()) socket.nudge();
    void this.refreshUnread();
    if (this.session) void this.syncPushDevice();
  }
  // ---------------------------------------------------------------- watching
  /**
   * Keeps the contact's recent conversations live in the background (call while the app is in
   * the foreground; `stopWatching` when it leaves). Agent replies then bump `unreadCount` and the
   * conversation's preview at once and are passed to `onAgentMessage` listeners, without marking
   * anything read. Only contacts with a session are watched: anonymous visitors that never chatted
   * have nothing to watch.
   */
  startWatching() {
    if (this.watching) return;
    this.watching = true;
    void this.syncWatch();
  }
  stopWatching() {
    this.watching = false;
    for (const socket of this.watchSockets.values()) socket.stop();
    this.watchSockets.clear();
  }
  /** Called for agent replies seen by a watch socket (not for the conversation open on screen). */
  onAgentMessage(listener) {
    this.agentMessageListeners.add(listener);
    return () => {
      this.agentMessageListeners.delete(listener);
    };
  }
  /** Opens watch sockets for the most recent conversations (and closes the rest). */
  async syncWatch() {
    if (!this.watching || !this.session) return;
    const epoch = this.epoch;
    if (!this.state.conversationsLoaded) {
      try {
        await this.mergeConversationPage();
      } catch {
        return;
      }
    }
    if (!this.watching || epoch !== this.epoch) return;
    const wanted = new Set(this.state.conversations.slice(0, MAX_WATCHED_CONVERSATIONS).map((c) => c.id));
    for (const [id, socket] of this.watchSockets) {
      if (!wanted.has(id)) {
        socket.stop();
        this.watchSockets.delete(id);
      }
    }
    for (const id of wanted) {
      if (this.watchSockets.has(id) || this.sockets.has(id)) continue;
      const socket = new ReconnectingSocket({
        url: () => this.http.socketUrl(id),
        WebSocket: this.opts.WebSocket,
        onEvent: (e) => this.handleWatchEvent(id, e),
        onStateChange: () => {
        },
        // Missed events while disconnected: the server's count is the truth.
        onReconnect: () => {
          void this.refreshUnread();
          void this.mergeConversationPage().catch(() => {
          });
        },
        onTerminalClose: () => {
          if (this.watchSockets.get(id) === socket) this.watchSockets.delete(id);
        }
      });
      this.watchSockets.set(id, socket);
      socket.start();
    }
  }
  /**
   * Loads the first conversation page into the list without dropping conversations it doesn't
   * contain (e.g. an older one open on screen), unlike `refreshConversations`.
   */
  async mergeConversationPage() {
    const epoch = this.epoch;
    const page = await this.http.listConversations();
    if (epoch !== this.epoch) return;
    this.store.set((s) => {
      const fresh = new Set(page.items.map((c) => c.id));
      return {
        conversations: [...page.items, ...s.conversations.filter((c) => !fresh.has(c.id))].sort((a, b) => b.lastMessageAt - a.lastMessageAt),
        conversationsLoaded: true
      };
    });
  }
  handleWatchEvent(conversationId, event) {
    if (this.sockets.has(conversationId)) return;
    if (event.type === "status.changed") {
      this.patchConversation(conversationId, { status: event.status });
      return;
    }
    if (event.type !== "message.created") return;
    const msg = event.message;
    this.patchConversation(conversationId, { lastMessage: msg, lastMessageAt: msg.createdAt });
    if (this.state.threads[conversationId]?.loaded) {
      this.updateThread(conversationId, (t) => ({ messages: mergeMessages(t.messages, [msg]) }));
    }
    if (msg.authorType !== "agent" || this.watchSeen.has(msg.id)) return;
    this.watchSeen.add(msg.id);
    this.store.set((s) => ({
      unreadCount: s.unreadCount + 1,
      conversations: s.conversations.map((c) => c.id === conversationId ? { ...c, unreadCount: c.unreadCount + 1 } : c)
    }));
    for (const listener of this.agentMessageListeners) {
      try {
        listener({ conversationId, message: msg });
      } catch {
      }
    }
  }
  /** Reloads a conversation whose last load failed (`thread.error`). */
  retryLoad(conversationId) {
    return this.loadLatest(conversationId);
  }
  loadLatest(conversationId, what = "full") {
    const inflight = this.loads.get(conversationId);
    if (inflight) {
      if (inflight.again !== "full") inflight.again = what;
      inflight.next ??= new Promise((resolve) => {
        inflight.resolveNext = resolve;
      });
      return inflight.next;
    }
    const epoch = this.epoch;
    const entry = { again: "none" };
    this.loads.set(conversationId, entry);
    void this.fetchLatest(conversationId, what).finally(() => {
      if (this.loads.get(conversationId) === entry) this.loads.delete(conversationId);
      const followUp = entry.again !== "none" && epoch === this.epoch ? this.loadLatest(conversationId, entry.again) : Promise.resolve();
      void followUp.then(() => entry.resolveNext?.());
      entry.resolveFirst?.();
    });
    return new Promise((resolve) => {
      entry.resolveFirst = resolve;
    });
  }
  async fetchLatest(conversationId, what) {
    const epoch = this.epoch;
    const thread = this.state.threads[conversationId];
    const withConversation = what === "full" || !thread?.loaded;
    this.updateThread(conversationId, () => ({ loading: true, error: false }));
    try {
      const [page, conversation] = await Promise.all([
        this.http.listMessages(conversationId),
        withConversation ? this.http.getConversation(conversationId) : null
      ]);
      if (epoch !== this.epoch) return;
      this.updateThread(conversationId, (t) => ({
        messages: mergeMessages(t.messages, page.items),
        hasOlder: thread?.loaded ? t.hasOlder || page.nextCursor !== null : page.nextCursor !== null,
        loaded: true,
        loading: false,
        error: false,
        agentLastReadAt: Math.max(t.agentLastReadAt, conversation?.agentLastReadAt ?? 0)
      }));
      if (conversation) this.upsertConversation(conversation);
    } catch {
      if (epoch !== this.epoch) return;
      this.updateThread(conversationId, (t) => ({ loading: false, error: !t.loaded }));
    }
  }
  async loadOlder(conversationId) {
    const thread = this.state.threads[conversationId];
    const oldest = thread?.messages.find((m) => m.status === "sent");
    if (!thread?.hasOlder || thread.loading || !oldest) return;
    const epoch = this.epoch;
    this.updateThread(conversationId, () => ({ loading: true }));
    try {
      const page = await this.http.listMessages(conversationId, oldest.id);
      if (epoch !== this.epoch) return;
      this.updateThread(conversationId, (t) => ({
        messages: mergeMessages(t.messages, page.items),
        hasOlder: page.nextCursor !== null,
        loading: false
      }));
    } catch {
      if (epoch !== this.epoch) return;
      this.updateThread(conversationId, () => ({ loading: false }));
    }
  }
  async markRead(conversationId) {
    const epoch = this.epoch;
    try {
      await this.http.markRead(conversationId);
      if (epoch !== this.epoch) return;
      if (!this.state.conversations.some((c) => c.id === conversationId)) {
        await this.refreshUnread();
        return;
      }
      this.store.set((s) => {
        const n = s.conversations.find((c) => c.id === conversationId)?.unreadCount ?? 0;
        return {
          unreadCount: Math.max(0, s.unreadCount - n),
          conversations: s.conversations.map((c) => c.id === conversationId ? { ...c, unreadCount: 0, contactLastReadAt: Date.now() } : c)
        };
      });
    } catch {
    }
  }
  /** Trailing debounce for read receipts while a conversation is on screen. */
  scheduleRead(conversationId) {
    clearTimeout(this.readTimers.get(conversationId));
    this.readTimers.set(
      conversationId,
      setTimeout(() => {
        this.readTimers.delete(conversationId);
        void this.markRead(conversationId);
      }, READ_DEBOUNCE_MS)
    );
  }
  handleEvent(conversationId, event) {
    switch (event.type) {
      case "message.created": {
        const msg = event.message;
        this.updateThread(conversationId, (t) => ({
          messages: mergeMessages(t.messages, [msg]),
          typing: msg.authorType === "agent" ? null : t.typing
        }));
        this.patchConversation(conversationId, { lastMessage: msg, lastMessageAt: msg.createdAt });
        if (msg.authorType !== "contact") this.scheduleRead(conversationId);
        break;
      }
      case "typing": {
        if (event.authorType !== "agent") return;
        const existing = this.typingIn.get(conversationId);
        if (existing) clearTimeout(existing);
        if (event.typing) {
          this.typingIn.set(
            conversationId,
            setTimeout(() => this.updateThread(conversationId, () => ({ typing: null })), REMOTE_TYPING_TTL_MS)
          );
        }
        this.updateThread(conversationId, () => ({ typing: event.typing ? { name: event.name } : null }));
        break;
      }
      case "read":
        if (event.authorType === "agent") {
          this.updateThread(conversationId, (t) => ({ agentLastReadAt: Math.max(t.agentLastReadAt, event.at) }));
        }
        break;
      case "status.changed":
        this.patchConversation(conversationId, { status: event.status });
        break;
      case "pong":
        break;
    }
  }
  // ---------------------------------------------------------------- sending
  /**
   * Sends a message with optimistic UI. With `conversationId = null` it starts a new
   * conversation (shown under DRAFT_CONVERSATION until created). Resolves with the
   * conversation id; failures leave the message marked "failed" for `retry`.
   */
  async sendMessage(conversationId, input) {
    const clientId = `c_${secureRandomId(12)}`;
    const threadId = conversationId ?? DRAFT_CONVERSATION;
    const optimistic = {
      id: `local:${clientId}`,
      conversationId: threadId,
      clientId,
      authorType: "contact",
      author: { id: this.state.contact?.id ?? null, name: this.state.contact?.name ?? null, avatarUrl: null },
      body: input.body,
      attachments: input.attachments ?? [],
      systemEvent: null,
      createdAt: Date.now(),
      status: "sending"
    };
    this.outbox.set(clientId, { conversationId, body: input.body, attachmentIds: input.attachmentIds ?? [] });
    this.updateThread(threadId, (t) => ({ messages: sortMessages([...t.messages, optimistic]), loaded: true }));
    if (conversationId) this.setTyping(conversationId, false);
    return this.deliver(clientId);
  }
  async retry(clientId) {
    const pending = this.outbox.get(clientId);
    if (!pending) return null;
    const threadId = pending.conversationId ?? DRAFT_CONVERSATION;
    this.setLocalStatus(threadId, clientId, "sending");
    return this.deliver(clientId);
  }
  setLocalStatus(threadId, clientId, status) {
    this.updateThread(threadId, (t) => ({
      messages: t.messages.map((m) => m.clientId === clientId && m.status !== "sent" ? { ...m, status } : m)
    }));
  }
  async deliver(clientId) {
    const epoch = this.epoch;
    let pending = this.outbox.get(clientId);
    while (pending && !pending.conversationId && this.startingConversation) {
      await this.startingConversation;
      if (epoch !== this.epoch) return null;
      pending = this.outbox.get(clientId);
    }
    if (!pending) return null;
    const req = { clientId, body: pending.body, attachmentIds: pending.attachmentIds };
    try {
      if (pending.conversationId) {
        const message2 = await this.http.sendMessage(pending.conversationId, req);
        if (epoch !== this.epoch) return null;
        this.outbox.delete(clientId);
        this.updateThread(pending.conversationId, (t) => ({ messages: mergeMessages(t.messages, [message2]) }));
        this.patchConversation(pending.conversationId, { lastMessage: message2, lastMessageAt: message2.createdAt });
        return pending.conversationId;
      }
      const start = this.http.startConversation(req);
      this.startingConversation = start.then(
        (r) => r.conversation.id,
        () => null
      );
      let started;
      try {
        started = await start;
      } finally {
        if (epoch === this.epoch) this.startingConversation = null;
      }
      if (epoch !== this.epoch) return null;
      const { conversation, message } = started;
      this.outbox.delete(clientId);
      this.store.set((s) => {
        const { [DRAFT_CONVERSATION]: draft, ...threads } = s.threads;
        const thread = {
          ...emptyThread(),
          messages: mergeMessages(
            (draft?.messages ?? []).filter((m) => m.clientId !== clientId),
            [message]
          ).map((m) => ({ ...m, conversationId: conversation.id })),
          loaded: true
        };
        return { threads: { ...threads, [conversation.id]: thread } };
      });
      for (const [id, p] of this.outbox) {
        if (p.conversationId === null) this.outbox.set(id, { ...p, conversationId: conversation.id });
      }
      this.upsertConversation(conversation);
      return conversation.id;
    } catch {
      if (epoch !== this.epoch) return null;
      this.setLocalStatus(this.outbox.get(clientId)?.conversationId ?? DRAFT_CONVERSATION, clientId, "failed");
      return null;
    }
  }
  /** Discards a failed message. */
  discard(clientId) {
    const pending = this.outbox.get(clientId);
    if (!pending) return;
    this.outbox.delete(clientId);
    const threadId = pending.conversationId ?? DRAFT_CONVERSATION;
    this.updateThread(threadId, (t) => ({ messages: t.messages.filter((m) => m.clientId !== clientId || m.status === "sent") }));
  }
  /** Call on every keystroke with typing=true; throttled and auto-stopped after idle. */
  setTyping(conversationId, typing) {
    const socket = this.sockets.get(conversationId)?.socket;
    const state = this.typingOut.get(conversationId) ?? { lastSentAt: 0, idleTimer: null };
    if (state.idleTimer) clearTimeout(state.idleTimer);
    state.idleTimer = null;
    if (typing) {
      const now = Date.now();
      if (now - state.lastSentAt >= TYPING_RESEND_MS && socket?.send({ type: "typing", typing: true })) {
        state.lastSentAt = now;
      }
      state.idleTimer = setTimeout(() => this.setTyping(conversationId, false), TYPING_IDLE_MS);
      this.typingOut.set(conversationId, state);
    } else if (state.lastSentAt > 0) {
      socket?.send({ type: "typing", typing: false });
      this.typingOut.delete(conversationId);
    }
  }
  // ---------------------------------------------------------------- misc
  async submitCsat(conversationId, input) {
    const epoch = this.epoch;
    await this.http.submitCsat(conversationId, input);
    if (epoch !== this.epoch) return;
    this.patchConversation(conversationId, { csatScore: input.score });
  }
  /**
   * Validates and uploads a file; pass the returned attachment's id (and the attachment for
   * optimistic display) to `sendMessage`. Throws LiveChatApiError with code
   * "too_large" / "unsupported_type" for invalid files without hitting the network.
   */
  async uploadAttachment(file) {
    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      throw new LiveChatApiError(415, "unsupported_type", "This file type isn't supported");
    }
    if (file.size > MAX_ATTACHMENT_BYTES) throw new LiveChatApiError(413, "too_large", "File is too large");
    return this.http.uploadAttachment(file);
  }
  async registerPushDevice(device) {
    await this.http.registerPushDevice(device);
    this.pushDevice = device;
    this.pushPending = false;
  }
  /** Stops push notifications to this device (e.g. the user disabled them). */
  async unregisterPushDevice() {
    const device = this.pushDevice;
    if (!device) return;
    await this.http.unregisterPushDevice(device.token);
    this.pushDevice = null;
    this.pushPending = false;
  }
};

// src/color.ts
function onColor(hex) {
  const full = /^#[0-9a-f]{3}$/i.test(hex) ? `#${[...hex.slice(1)].map((c) => c + c).join("")}` : hex;
  const n = parseInt(full.slice(1), 16);
  const [r, g, b] = [n >> 16 & 255, n >> 8 & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const DARK_TEXT_LUMINANCE = 0.0137;
  const contrastWhite = 1.05 / (luminance + 0.05);
  const contrastDark = (luminance + 0.05) / (DARK_TEXT_LUMINANCE + 0.05);
  return contrastDark >= contrastWhite ? "#111827" : "#ffffff";
}

// src/markdown.ts
var SAFE_URL = /^(https?:\/\/|mailto:|tel:)/i;
function safeUrl(url) {
  const trimmed = url.trim();
  return SAFE_URL.test(trimmed) ? trimmed : null;
}
function parseInline(src, opts = {}) {
  const out = [];
  let text = "";
  const flush = () => {
    if (text) out.push({ type: "text", text });
    text = "";
  };
  let i = 0;
  while (i < src.length) {
    const rest = src.slice(i);
    let m;
    if (rest[0] === "\\" && rest.length > 1 && /[\\`*_[\]()!#>-]/.test(rest[1])) {
      text += rest[1];
      i += 2;
      continue;
    }
    if (rest[0] === "\n") {
      flush();
      out.push({ type: "break" });
      i++;
      continue;
    }
    if (m = /^`([^`]+)`/.exec(rest)) {
      flush();
      out.push({ type: "code", text: m[1] });
      i += m[0].length;
      continue;
    }
    if (m = /^!\[([^\]]*)\]\(([^)\s]+)\)/.exec(rest)) {
      const src2 = safeUrl(m[2]);
      if (src2 && /^https?:/i.test(src2)) {
        flush();
        out.push(opts.images ? { type: "image", src: src2, alt: m[1] } : { type: "link", href: src2, children: [{ type: "text", text: m[1] || src2 }] });
      } else {
        text += m[1];
      }
      i += m[0].length;
      continue;
    }
    if (m = /^\[([^\]]+)\]\(([^)\s]+)\)/.exec(rest)) {
      const href = safeUrl(m[2]);
      flush();
      if (href) out.push({ type: "link", href, children: parseInline(m[1], opts) });
      else out.push(...parseInline(m[1], opts));
      i += m[0].length;
      continue;
    }
    if ((m = /^(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/.exec(rest)) && (i === 0 || /\s/.test(src[i - 1]))) {
      flush();
      out.push({ type: "link", href: m[1], children: [{ type: "text", text: m[1] }] });
      i += m[0].length;
      continue;
    }
    if (m = /^(?:\*\*(?=\S)([\s\S]*?\S)\*\*(?!\*)|__(?=\S)([\s\S]*?\S)__(?!_))/.exec(rest)) {
      flush();
      out.push({ type: "strong", children: parseInline(m[1] ?? m[2], opts) });
      i += m[0].length;
      continue;
    }
    if ((m = /^(\*|_)(?=\S)([\s\S]*?\S)\1(?![*_])/.exec(rest)) && (m[1] === "*" || i === 0 || /\W/.test(src[i - 1]))) {
      flush();
      out.push({ type: "em", children: parseInline(m[2], opts) });
      i += m[0].length;
      continue;
    }
    text += rest[0];
    i++;
  }
  flush();
  return out;
}
function parseMarkdown(md, opts = {}) {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  const isBlockStart = (line) => /^(#{1,4})\s/.test(line) || /^```/.test(line) || /^>\s?/.test(line) || /^\s*([-*+]|\d+[.)])\s+/.test(line) || /^(\*{3,}|-{3,}|_{3,})\s*$/.test(line);
  while (i < lines.length) {
    const line = lines[i];
    let m;
    if (!line.trim()) {
      i++;
      continue;
    }
    if (/^```/.test(line)) {
      const body = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) body.push(lines[i++]);
      i++;
      blocks.push({ type: "codeblock", text: body.join("\n") });
      continue;
    }
    if (m = /^(#{1,4})\s+(.*?)\s*#*$/.exec(line)) {
      blocks.push({ type: "heading", level: m[1].length, children: parseInline(m[2], opts) });
      i++;
      continue;
    }
    if (/^(\*{3,}|-{3,}|_{3,})\s*$/.test(line)) {
      blocks.push({ type: "rule" });
      i++;
      continue;
    }
    if (/^>\s?/.test(line)) {
      const body = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) body.push(lines[i++].replace(/^>\s?/, ""));
      blocks.push({ type: "quote", children: parseMarkdown(body.join("\n"), opts) });
      continue;
    }
    if (m = /^\s*([-*+]|\d+[.)])\s+/.exec(line)) {
      const ordered = /\d/.test(m[1]);
      const items = [];
      while (i < lines.length) {
        const item = /^\s*([-*+]|\d+[.)])\s+(.*)$/.exec(lines[i]);
        if (item && /\d/.test(item[1]) === ordered) {
          items.push(item[2]);
        } else if (items.length && /^\s{2,}\S/.test(lines[i])) {
          items[items.length - 1] += `
${lines[i].trim()}`;
        } else {
          break;
        }
        i++;
      }
      blocks.push({ type: "list", ordered, items: items.map((item) => parseInline(item, opts)) });
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() && !isBlockStart(lines[i])) para.push(lines[i++]);
    blocks.push({ type: "paragraph", children: parseInline(para.join("\n"), opts) });
  }
  return blocks;
}

// src/index.ts
import {
  ALLOWED_ATTACHMENT_TYPES as ALLOWED_ATTACHMENT_TYPES2,
  CLOSE_ACCESS_REVOKED,
  CLOSE_SESSION_REVOKED as CLOSE_SESSION_REVOKED2,
  MAX_ATTACHMENT_BYTES as MAX_ATTACHMENT_BYTES2,
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_MESSAGE_LENGTH
} from "@kobecuppens/livechat-protocol/constants";
export {
  ALLOWED_ATTACHMENT_TYPES2 as ALLOWED_ATTACHMENT_TYPES,
  CLOSE_ACCESS_REVOKED,
  CLOSE_SESSION_REVOKED2 as CLOSE_SESSION_REVOKED,
  DRAFT_CONVERSATION,
  EMPTY_THREAD,
  LiveChatApiError,
  LiveChatClient,
  LiveChatHttp,
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_ATTACHMENT_BYTES2 as MAX_ATTACHMENT_BYTES,
  MAX_MESSAGE_LENGTH,
  ReconnectingSocket,
  Store,
  createMemoryStorage,
  createTranslator,
  createWebStorage,
  localeChain,
  negotiateLocale,
  onColor,
  parseInline,
  parseMarkdown,
  safeUrl,
  secureRandomId
};
